import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import {
  getCustomMaterialsQueryData,
  setCustomMaterialsQueryData,
  customMaterialsQuery,
} from "#modules/materials/infra/customMaterials.query";
import { updateMaterialApi } from "#modules/materials/infra/updateMaterial.api";

import { materialsQuery } from "../infra/materials.query";

export const useUpdateMaterial = () => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: updateMaterialApi,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: customMaterialsQuery().queryKey });
      const previous = getCustomMaterialsQueryData(queryClient);

      if (previous) {
        setCustomMaterialsQueryData(
          queryClient,
          previous.map((material) =>
            material.id === variables.id ? { ...material, ...variables.payload } : material,
          ),
        );
      }

      return { previous };
    },
    onSuccess: (data) => {
      const current = getCustomMaterialsQueryData(queryClient);

      if (current) {
        setCustomMaterialsQueryData(
          queryClient,
          current.map((material) => (material.id === data.id ? data : material)),
        );
      }
    },
    onError: (err: Error, _variables, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.previous) {
        setCustomMaterialsQueryData(queryClient, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries(customMaterialsQuery());
      void queryClient.invalidateQueries(materialsQuery());
    },
  });

  return { updateMaterial: mutate, ...rest };
};
